import { useEffect, useMemo, useState } from "react";

import { useIsFetching } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { companyAnalysisQueryKeys } from "app/pages/sales-intelligence/modules/company-analysis/queries/queryKeyFactory";
import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type { SignalEventType } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { isDropdownOpenAction } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type UseSignalEventsFilterProps = {
    signalEvents: readonly SignalEventDto[];
};

type UseSignalEventsFilterReturnProps = {
    filterValue: SignalEventType;
    filteredSignalEvents: readonly SignalEventDto[];
    handleFilterChange: (value: SignalEventType) => void;
};

const useSignalEventsFilter = ({ signalEvents }: UseSignalEventsFilterProps): UseSignalEventsFilterReturnProps => {
    const dispatch = useDispatch();
    const isSignalEventsLoading = useIsFetching({ queryKey: companyAnalysisQueryKeys.signalEvents() });
    const [filterValue, setFilterValue] = useState<SignalEventType>("All");

    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const { activeSignalFilter, isShowManageSignals } = params;

    useEffect(() => {
        if (isSignalEventsLoading || (!activeSignalFilter && !isShowManageSignals)) {
            return;
        }

        if (activeSignalFilter) {
            setFilterValue(activeSignalFilter);
        }
        if (isShowManageSignals) {
            dispatch(isDropdownOpenAction(true));
        }

        void proNavigator.updateParams(
            {
                activeSignalFilter: undefined,
                isShowManageSignals: undefined,
            },
            {
                location: "replace",
            },
        );
    }, [isSignalEventsLoading, activeSignalFilter, isShowManageSignals]);

    const handleFilterChange = (value: SignalEventType) => {
        setFilterValue(value);
    };

    const filteredSignalEvents = useMemo(
        () => signalEventsService.getSignalEventsOfType(signalEvents, filterValue),
        [signalEvents, filterValue],
    );

    return {
        filterValue,
        filteredSignalEvents,
        handleFilterChange,
    };
};

export default useSignalEventsFilter;
